import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Products', path: '/commodities' },
    { name: 'Countries', path: '/countries' },
    { name: 'Insights', path: '/insights' },
    { name: 'Reports', path: '/reports' },
    { name: 'Energia IQ', path: '/energia-iq' }
  ];

  // Comprehensive search index covering all pages, content, and keywords
  const searchIndex = [
    // Home Page
    { name: 'Home', path: '/', category: 'Page', keywords: ['home', 'enerlligence', 'analytics', 'energy intelligence', 'connected insights', 'forward thinking', 'global energy', 'proprietary data', 'market research', 'strategic insights', 'energy transition', 'investment decisions', 'policy decisions'] },
    { name: 'Intelligence Services', path: '/', category: 'Section', keywords: ['commodity research', 'country analysis', 'market intelligence', 'emissions tracking', 'specialized reports', 'intelligence services'] },
    { name: 'Insights & Thought Leadership', path: '/', category: 'Section', keywords: ['insights', 'thought leadership', 'expert analysis', 'market intelligence', 'strategic commentary', 'global energy markets'] },
    { name: 'Why Industry Leaders Trust Enerlligence', path: '/', category: 'Section', keywords: ['industry leaders', 'trust', 'fortune 500', 'sovereign wealth funds', 'government agencies'] },
    
    // About Pages
    { name: 'About Enerlligence', path: '/about', category: 'Page', keywords: ['about', 'company', 'enerlligence analytics', 'energy intelligence platform', 'credible insights', 'analytical clarity'] },
    { name: 'Our Story', path: '/about/story', category: 'Subpage', keywords: ['story', 'history', 'founding', 'mission', 'vision', 'energy intelligence infrastructure', 'strategic decision-making'] },
    { name: 'Our People', path: '/about/people', category: 'Subpage', keywords: ['people', 'team', 'leadership', 'experts', 'analysts', 'multidisciplinary expertise', 'energy commodities', 'financial markets'] },
    { name: 'Our Values', path: '/about/values', category: 'Subpage', keywords: ['values', 'principles', 'ethics', 'integrity', 'intelligence delivery', 'research', 'analysis', 'strategic insight'] },
    { name: 'Our Culture', path: '/about/culture', category: 'Subpage', keywords: ['culture', 'workplace', 'environment', 'intellectual rigor', 'collaborative excellence', 'commitment to impact'] },
    { name: 'Our Customers', path: '/about/customers', category: 'Subpage', keywords: ['customers', 'clients', 'partners', 'trusted intelligence partner', 'global energy ecosystem', 'strategic decision-makers'] },
    { name: 'Our Locations', path: '/about/locations', category: 'Subpage', keywords: ['locations', 'offices', 'global coverage', '200 countries', 'digital-first intelligence'] },
    
    // Products Page
    { name: 'Products', path: '/commodities', category: 'Page', keywords: ['products', 'product portfolio', 'intelligence platforms', 'proprietary data', 'market analysis', 'price forecasts', 'strategic insights'] },
    { name: 'Commodity Prices', path: '/commodities', category: 'Product', keywords: ['commodity prices', 'price intelligence', 'real-time prices', 'historical trends', 'forecasting models', 'crude oil prices', 'natural gas prices', 'coal prices', 'carbon credits'] },
    { name: 'Crude Oil Intelligence', path: '/commodities', category: 'Product', keywords: ['crude oil', 'oil', 'petroleum', 'upstream', 'refinery', 'trade flows', 'oil prices', 'reserves', 'production', 'consumption', 'refinery capacity'] },
    { name: 'Natural Gas Intelligence', path: '/commodities', category: 'Product', keywords: ['natural gas', 'gas', 'lng', 'liquefied natural gas', 'pipeline', 'hydrogen', 'gas prices', 'reserves', 'production', 'consumption', 'liquefaction capacity'] },
    { name: 'Coal Intelligence', path: '/commodities', category: 'Product', keywords: ['coal', 'thermal coal', 'metallurgical coal', 'coal prices', 'reserves', 'production', 'consumption', 'trade movements'] },
    { name: 'Solar Energy Intelligence', path: '/commodities', category: 'Product', keywords: ['solar', 'solar energy', 'photovoltaic', 'pv', 'solar panels', 'installed capacity', 'solar installations'] },
    { name: 'Wind Energy Intelligence', path: '/commodities', category: 'Product', keywords: ['wind', 'wind energy', 'wind power', 'turbines', 'onshore wind', 'offshore wind', 'installed capacity'] },
    { name: 'Geothermal Energy Intelligence', path: '/commodities', category: 'Product', keywords: ['geothermal', 'geothermal energy', 'steam fields', 'geothermal plants', 'geothermal production'] },
    { name: 'Nuclear Energy Intelligence', path: '/commodities', category: 'Product', keywords: ['nuclear', 'nuclear energy', 'nuclear power', 'reactors', 'uranium', 'nuclear production'] },
    { name: 'Bio Energy Intelligence', path: '/commodities', category: 'Product', keywords: ['bioenergy', 'bio energy', 'biomass', 'biofuels', 'organic feedstock', 'bioenergy production'] },
    { name: 'Hydropower Intelligence', path: '/commodities', category: 'Product', keywords: ['hydropower', 'hydroelectric', 'dams', 'water resources', 'hydro production'] },
    { name: 'Critical Minerals Intelligence', path: '/commodities', category: 'Product', keywords: ['critical minerals', 'lithium', 'cobalt', 'rare earth', 'nickel', 'copper', 'graphite', 'battery metals', 'energy transition minerals'] },
    { name: 'Emissions Tracking', path: '/commodities#emissions-detail', category: 'Product', keywords: ['emissions', 'emissions tracking', 'carbon', 'greenhouse gas', 'ghg', 'carbon markets', 'decarbonization', 'climate', 'net zero', 'carbon pricing', 'eu ets', 'carbon credits', 'carbon offsets'] },
    
    // Countries Page
    { name: 'Countries', path: '/countries', category: 'Page', keywords: ['countries', 'country intelligence', 'regional intelligence', 'global markets', 'analytical depth', 'strategic insight'] },
    { name: 'Africa Region', path: '/countries', category: 'Region', keywords: ['africa', 'african', 'nigeria', 'south africa', 'egypt', 'angola', 'algeria', 'mozambique', 'ghana', 'kenya'] },
    { name: 'Asia Pacific Region', path: '/countries', category: 'Region', keywords: ['asia', 'asia pacific', 'apac', 'china', 'india', 'japan', 'australia', 'indonesia', 'vietnam', 'thailand', 'malaysia', 'singapore', 'south korea'] },
    { name: 'Europe Region', path: '/countries', category: 'Region', keywords: ['europe', 'european', 'uk', 'united kingdom', 'germany', 'france', 'netherlands', 'norway', 'italy', 'spain', 'poland'] },
    { name: 'Americas Region', path: '/countries', category: 'Region', keywords: ['americas', 'north america', 'south america', 'latin america', 'usa', 'united states', 'canada', 'brazil', 'mexico', 'argentina', 'colombia', 'chile', 'peru'] },
    { name: 'Middle East Region', path: '/countries', category: 'Region', keywords: ['middle east', 'mena', 'saudi arabia', 'uae', 'qatar', 'kuwait', 'iraq', 'iran', 'oman', 'bahrain'] },
    { name: 'Russia & CIS Region', path: '/countries', category: 'Region', keywords: ['russia', 'cis', 'kazakhstan', 'uzbekistan', 'turkmenistan', 'azerbaijan'] },
    
    // Insights Page
    { name: 'Insights', path: '/insights', category: 'Page', keywords: ['insights', 'market insights', 'analysis', 'expert commentary', 'research publications', 'intelligence briefings', 'energy trends', 'mining markets'] },
    { name: 'Energy Markets Insights', path: '/insights', category: 'Topic', keywords: ['energy markets', 'market analysis', 'supply demand', 'price forecasts', 'market dynamics'] },
    { name: 'Energy Transition Insights', path: '/insights', category: 'Topic', keywords: ['energy transition', 'decarbonization', 'renewable energy', 'clean energy', 'net zero', 'climate policy'] },
    { name: 'Geopolitics Insights', path: '/insights', category: 'Topic', keywords: ['geopolitics', 'geopolitical', 'political risk', 'sanctions', 'trade policy', 'energy security'] },
    { name: 'Commodity Intelligence', path: '/insights', category: 'Topic', keywords: ['commodity intelligence', 'commodity markets', 'supply chains', 'trading', 'hedging'] },
    
    // Reports Page
    { name: 'Reports', path: '/reports', category: 'Page', keywords: ['reports', 'research reports', 'institutional-grade research', 'intelligence', 'publications', 'data analytics'] },
    { name: 'Oil & Gas Reports', path: '/reports', category: 'Report Category', keywords: ['oil reports', 'gas reports', 'petroleum reports', 'lng reports', 'upstream reports', 'downstream reports'] },
    { name: 'Power & Renewables Reports', path: '/reports', category: 'Report Category', keywords: ['power reports', 'renewables reports', 'electricity reports', 'solar reports', 'wind reports'] },
    { name: 'Mining & Metals Reports', path: '/reports', category: 'Report Category', keywords: ['mining reports', 'metals reports', 'critical minerals reports', 'battery metals reports'] },
    { name: 'Climate & Carbon Reports', path: '/reports', category: 'Report Category', keywords: ['climate reports', 'carbon reports', 'emissions reports', 'sustainability reports', 'esg reports'] },
    
    // Energia IQ Page
    { name: 'Energia IQ', path: '/energia-iq', category: 'Page', keywords: ['energia iq', 'intelligence platform', 'proprietary datasets', 'analytical models', 'market intelligence', 'global energy systems'] },
    { name: 'Oil IQ Platform', path: '/energia-iq', category: 'Platform', keywords: ['oil iq', 'crude oil intelligence', 'oil assets', 'refining infrastructure', 'oil trade flows'] },
    { name: 'Gas IQ Platform', path: '/energia-iq', category: 'Platform', keywords: ['gas iq', 'natural gas intelligence', 'lng infrastructure', 'pipeline networks', 'gas supply chains'] },
    { name: 'Power IQ Platform', path: '/energia-iq', category: 'Platform', keywords: ['power iq', 'electricity intelligence', 'power generation', 'grid infrastructure', 'power markets'] },
    { name: 'Minerals IQ Platform', path: '/energia-iq', category: 'Platform', keywords: ['minerals iq', 'critical minerals intelligence', 'battery supply chains', 'rare earth intelligence'] },
    { name: 'Decision Intelligence Workflows', path: '/energia-iq', category: 'Feature', keywords: ['asset discovery', 'infrastructure intelligence', 'market forecasting', 'investment screening', 'energy transition analysis', 'policy monitoring'] },
    { name: 'Platform Capabilities', path: '/energia-iq', category: 'Feature', keywords: ['data visualization', 'scenario modeling', 'benchmarking', 'alerts', 'custom reports', 'api access'] },
    
    // Contact Page
    { name: 'Contact Us', path: '/contact', category: 'Page', keywords: ['contact', 'enquiry', 'support', 'email', 'phone', 'get in touch', 'request demo', 'schedule consultation'] },
    
    // Industry Terms
    { name: 'LNG Intelligence', path: '/commodities', category: 'Topic', keywords: ['lng', 'liquefied natural gas', 'lng terminal', 'lng export', 'lng import', 'regasification', 'liquefaction'] },
    { name: 'Refinery Intelligence', path: '/commodities', category: 'Topic', keywords: ['refinery', 'refineries', 'refining', 'downstream', 'petrochemical', 'fuel production'] },
    { name: 'Pipeline Intelligence', path: '/energia-iq', category: 'Topic', keywords: ['pipeline', 'pipelines', 'gas pipeline', 'oil pipeline', 'transport', 'midstream'] },
    { name: 'Carbon Markets', path: '/commodities#emissions-detail', category: 'Topic', keywords: ['carbon markets', 'carbon trading', 'ets', 'cap and trade', 'carbon allowances', 'voluntary carbon', 'carbon offsets'] },
    { name: 'Energy Investment', path: '/energia-iq', category: 'Topic', keywords: ['investment', 'capital', 'financing', 'project finance', 'energy investment', 'infrastructure investment'] },
    { name: 'Supply Chain Intelligence', path: '/commodities', category: 'Topic', keywords: ['supply chain', 'logistics', 'trade flows', 'shipping', 'transport', 'distribution'] },
    { name: 'Price Forecasting', path: '/commodities', category: 'Topic', keywords: ['price forecast', 'price outlook', 'price prediction', 'market forecast', 'commodity forecast'] },
    { name: 'Regulatory Intelligence', path: '/insights', category: 'Topic', keywords: ['regulatory', 'regulations', 'policy', 'compliance', 'government policy', 'energy policy'] },
    
    // Client Types
    { name: 'Solutions for Energy Companies', path: '/about/customers', category: 'Client', keywords: ['energy companies', 'oil companies', 'gas companies', 'utilities', 'power companies'] },
    { name: 'Solutions for Investors', path: '/about/customers', category: 'Client', keywords: ['investors', 'investment banks', 'private equity', 'hedge funds', 'asset managers', 'sovereign wealth'] },
    { name: 'Solutions for Governments', path: '/about/customers', category: 'Client', keywords: ['governments', 'government agencies', 'ministries', 'regulators', 'policymakers', 'public sector'] },
    { name: 'Solutions for Consultants', path: '/about/customers', category: 'Client', keywords: ['consultants', 'consulting firms', 'advisors', 'advisory', 'strategy consulting'] }
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchOpen && !event.target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (filteredResults.length > 0) {
      navigate(filteredResults[0].path);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const filteredResults = searchQuery.trim().length >= 2
    ? searchIndex.filter(item => {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.keywords.some(keyword => keyword.includes(query))
        );
      }).slice(0, 10) // Limit to 10 results
    : [];

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_22bcd9cf-3cc7-43c2-99a5-951906bdc6ea/artifacts/rqym1a70_2.png" 
              alt="Enerlligence Analytics" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center justify-center flex-1 px-8">
            <div className="flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-white bg-[#007BFF]'
                      : 'text-[#0F2A4D] hover:text-[#007BFF] hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Section - Contact Us + Search */}
          <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
            <Link
              to="/contact"
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md whitespace-nowrap ${
                isActive('/contact')
                  ? 'text-white bg-[#007BFF]'
                  : 'text-[#0F2A4D] hover:text-[#007BFF] hover:bg-slate-50'
              }`}
            >
              Contact Us
            </Link>
            
            {/* Search Button */}
            <div className="relative search-container">
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`p-2 rounded-md transition-colors ${
                  isSearchOpen 
                    ? 'bg-[#007BFF] text-white' 
                    : 'text-[#0F2A4D] hover:bg-slate-100 hover:text-[#007BFF]'
                }`}
                aria-label="Search"
              >
                <Search size={20} />
              </button>

              {/* Search Dropdown */}
              {isSearchOpen && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden z-50">
                  <form onSubmit={handleSearch} className="p-3 border-b border-slate-100 bg-slate-50">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search pages, products, regions, topics..."
                        className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:border-transparent bg-white"
                      />
                    </div>
                  </form>
                  
                  {/* Search Results */}
                  <div className="max-h-80 overflow-y-auto">
                    {searchQuery.trim().length < 2 ? (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        Type at least 2 characters to search...
                      </div>
                    ) : filteredResults.length > 0 ? (
                      filteredResults.map((result, index) => (
                        <Link
                          key={index}
                          to={result.path}
                          onClick={() => {
                            setSearchQuery('');
                            setIsSearchOpen(false);
                          }}
                          className="block px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-[#0F2A4D]">{result.name}</span>
                            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{result.category}</span>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        No results found for "{searchQuery}"
                      </div>
                    )}
                  </div>
                  
                  {/* Search Tips */}
                  {searchQuery.trim().length >= 2 && filteredResults.length > 0 && (
                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
                      Press Enter to go to first result
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-[#0F2A4D] hover:bg-slate-100 transition-colors ml-auto"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          {/* Mobile Search */}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Enerlligence..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:border-transparent"
                />
              </div>
            </form>
            {/* Mobile Search Results */}
            {searchQuery.trim().length >= 2 && (
              <div className="mt-2 max-h-48 overflow-y-auto bg-white rounded-md border border-slate-200">
                {filteredResults.length > 0 ? (
                  filteredResults.slice(0, 5).map((result, index) => (
                    <Link
                      key={index}
                      to={result.path}
                      onClick={() => {
                        setSearchQuery('');
                        setIsOpen(false);
                      }}
                      className="block px-3 py-2 text-sm text-[#0F2A4D] hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <span className="font-medium">{result.name}</span>
                      <span className="text-xs text-slate-400 ml-2">({result.category})</span>
                    </Link>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-slate-500">No results found</div>
                )}
              </div>
            )}
          </div>
          
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 text-base font-medium rounded-md transition-colors ${
                  isActive(link.path)
                    ? 'text-white bg-[#007BFF]'
                    : 'text-[#0F2A4D] hover:bg-slate-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-3 text-base font-medium rounded-md transition-colors ${
                isActive('/contact')
                  ? 'text-white bg-[#007BFF]'
                  : 'text-[#0F2A4D] hover:bg-slate-50'
              }`}
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
